import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Card, Form, Row, Col, Alert, Button, ListGroup,
} from 'react-bootstrap';
import moment from 'moment';

import Required from '../../Required';
import AddSelection from '../Partials/AddSelection';

import { status as PS } from '../../../helpers/passport';
import useToast from '../../../hooks/useToast';
import { checkDate, checkDatesPair } from './helpers/validation';
import TooltippedElement from '../../common/TooltippedElement';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import useAPI from '../../../hooks/useAPI';
import { Tooltip } from '@material-ui/core';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';

const default_labelsCfg = {
  numLabel: 'Номер проекта',
  nameLabel: 'Название',
  namePlaceholder: 'Введите название проекта',
  startDateLabel: 'Дата начала проекта',
  endDateLabel: 'Дата окончания проекта',
  typeLabel: 'Тип проекта',
  startDateErrorMsg: 'Дата начала проекта должна быть не ранее текущей даты',
  endDateErrorMsg: 'Дата окончания проекта должна быть не ранее текущей даты',
  parentGoalsLabel: 'Дополнительные стратегические цели, на которые влияет проект',
  parentPoliciesLabel: 'Политики развития университета, на которые влияет проект',
  partnersLabel: 'Партнеры проекта',
};

const categories = [
  { id: 1, title: '1 категория', tooltip: 'Выполнение НИОКР совместно с приглашенным ведущим ученым, занимающим лидирующие позиции в области наук' },
  { id: 2, title: '2 категория', tooltip: 'Выполнение НИОКР в рамках взаимодействия с индустриальным партнером' },
  { id: 3, title: '3 категория', tooltip: 'Выполнение НИОКР вновь созданной молодежной научной лабораторией' },
];

export default function CommonInfo(props) {
  // COMMON PROPS
  const {
    data, onChange, types, kpis, policies, labelsCfg, validated, disabled
  } = props;
  // PORTFOLIO SPECIFIC PROPS
  const {
    minDate, maxDate, isPresetTypeId, portfolio, withCategory,
  } = props;
  const portfolioName = portfolio && portfolio.name;
  const currentYear = new Date().getFullYear();

  const isNotFirstVersion = data.version && data.version > 1;
  const canEditApprovedOn = [PS.approved, PS['-approved']].includes(data.status);
  const labels = useMemo(() => ({ ...default_labelsCfg, ...(labelsCfg || {}) }), [labelsCfg]);

  const [endToEndTechsOptions, setEndToEndTechsOptions] = useState([]);
  const [criticalTechsOptions, setCriticalTechsOptions] = useState([]);
  const [sciTechDirectionsOptions, setSciTechDirectionsOptions] = useState([]);
  const [majorSciTechDevChallengesOptions, setMajorSciTechDevChallengesOptions] = useState([]);
  const [sciTechDevUrFUDirectionsOptions, setSciTechDevUrFUDirectionsOptions] = useState([]);
  const [nationalProjTechLeadsOptions, setNationalProjTechLeadsOptions] = useState([]);
  const [parentGoalsOptions, setParentGoalsOptions] = useState([]);
  const [parentPoliciesOptions, setParentPoliciesOptions] = useState([]);
  const api = useAPI();

  const getStartDateError = () => {
    if (!data.startDate) return 'Обязательное поле к заполнению';
    const portfolioChecks = !portfolio ? null : (
      checkDatesPair(data.startDate, portfolio.endDate, 'Дата начала проекта вне срока реализации портфеля').error
      || checkDatesPair(portfolio.startDate, data.startDate, 'Дата начала проекта вне срока реализации портфеля').error
    );
    return checkDate(data.startDate).error
      || checkDatesPair(data.startDate, data.endDate).error
      || portfolioChecks;
  };

  const getEndDateError = () => {
    if (!data.endDate) return 'Обязательное поле к заполнению';
    const portfolioChecks = !portfolio ? null : (
      checkDatesPair(data.endDate, portfolio.endDate, 'Дата окончания проекта вне срока реализации портфеля').error
      || checkDatesPair(portfolio.startDate, data.endDate, 'Дата окончания проекта вне срока реализации портфеля').error
    );
    return checkDate(data.endDate).error
      || checkDatesPair(data.startDate, data.endDate).error
      || portfolioChecks;
  };

  const startDateError = getStartDateError();
  const endDateError = getEndDateError();

  const changeCategory = (e) => {
    onChange(parseInt(e.target.value), 'categoryId');
  };

  const timeoutId = useRef()
  const loadGrntiOptions = (inputValue, callback) => {
    clearTimeout(timeoutId.current)
    timeoutId.current = setTimeout(async () => {
      const grntis = await api.get(`/mng/grntis?name=${inputValue}&limit=20`);
      callback(
        grntis
          .filter(grnti => (data.grntis || []).every(value => value.id !== grnti.id))
          .map(value => ({label: `${value.num} ${value.name}`, value}))
      );
    }, 400)
  }

  const timeoutIdPartners = useRef();
  const loadPartnersOptions = (inputValue, callback) => {
    clearTimeout(timeoutIdPartners.current);
    timeoutIdPartners.current = setTimeout(async () => {
      try {
        const orgs = await api.get(`/mng/orgs/dadata?search=${inputValue}`);
        callback(
          orgs
            .filter(org => (data.partners || []).every(p => p.inn !== org.inn))
            .map(org => ({
              label: `${org.inn} ${org.title}`,
              value: org
            }))
        );
      } catch (error) {
        console.error('Error loading partners:', error);
        callback([]);
      }
    }, 400);
  };

  const [isScientific, setIsScientific] = useState(false);

  const checkIsScientific = (typeId) => {
    const selectedType = types.find(t => t.id == typeId);
    return selectedType?.name === 'Научный';
  };

  const handleTypeSelectChange = (value) => {
    onChange(value, 'typeId');
    setIsScientific(checkIsScientific(value));
  };

  useEffect(() => {
    setIsScientific(checkIsScientific(data.typeId));
  }, [data.typeId, types]);

  useEffect(() => {
    const fetchTechsOptions = async () => {
      const endToEndTechs = await api.get('/mng/endToEndTechs');
      const criticalTechs = await api.get('/mng/criticalTechs');
      const sciTechDirections = await api.get('/mng/sciTechDirections');
      const majorSciTechDevChallenges = await api.get('/mng/majorSciTechDevChallenges');
      const sciTechDevUrFUDirections = await api.get('/mng/sciTechDevUrFUDirections');
      const nationalProjTechLeads = await api.get('/mng/nationalProjTechLeads');
      const parentGoals = await api.get('/mng/strategies?type=goal');
      const parentPolicies = await api.get('/mng/strategies?type=policy');

      setEndToEndTechsOptions(endToEndTechs.map(value => ({label: value.name, value})))
      setCriticalTechsOptions(criticalTechs.map(value => ({label: value.name, value})))
      setSciTechDirectionsOptions(sciTechDirections.map(value => ({label: value.name, value})))
      setMajorSciTechDevChallengesOptions(majorSciTechDevChallenges.map(value => ({label: value.name, value})))
      setSciTechDevUrFUDirectionsOptions(sciTechDevUrFUDirections.map(value => ({label: value.name, value})))
      setNationalProjTechLeadsOptions(nationalProjTechLeads.map(value => ({label: value.name, value})))
      setParentGoalsOptions(parentGoals.map(value => ({label: value.name, value})))
      setParentPoliciesOptions(
        parentPolicies
          .filter((policy) => !policy.isArchived)
          .map((value) => ({ label: value.name, value }))
      );
    }
    fetchTechsOptions()
  }, []);

  return (
    <Card className="mb-4">
      <Card.Header as={Card.Title} className="p-4">Общая информация</Card.Header>
      <Card.Body className="p-4">
        <Row className="mb-4">
          <Form.Group as={Col} md="4">
            <Form.Label>{labels.numLabel}</Form.Label>
            <Form.Control
              placeholder="Номер присвоится автоматически (при сохранении)"
              value={data.fullNumber}
              type="text"
              disabled
              onChange={(elem) => onChange(elem.target.value, 'fullNumber')}
            />
          </Form.Group>
          <Form.Group as={Col} md="4">
            <Form.Label>Версия</Form.Label>
            <Form.Control
              value={data.version}
              type="number"
              onChange={(elem) => onChange(parseInt(elem.target.value), 'version')}
              disabled
            />
          </Form.Group>
          <Form.Group as={Col} md="4">
            <Form.Label>Дата утверждения версии</Form.Label>
            <Form.Control
              value={data.approvedOn}
              type="date"
              disabled={!canEditApprovedOn || disabled}
              onChange={(elem) => onChange(elem.target.value, 'approvedOn')}
            />
          </Form.Group>
        </Row>
        <Row className="mb-4">
          <Form.Group as={Col}>
            <Form.Label>
              {labels.nameLabel}
              {' '}
              <Required />
            </Form.Label>
            <Form.Control
              placeholder={labels.namePlaceholder}
              required
              as="textarea"
              rows={3}
              value={data.name}
              onChange={(elem) => onChange(elem.target.value, 'name')}
              disabled={disabled}
            />
            <Form.Control.Feedback type="invalid">Обязательное поле к заполнению</Form.Control.Feedback>
          </Form.Group>
        </Row>
        <Row className="mb-4">
          <Form.Group as={Col} md="6">
            <Form.Label>
              {labels.startDateLabel}
              {' '}
              <Required />
            </Form.Label>
            <Form.Control
              required
              type="date"
              value={data.startDate}
              onChange={(elem) => onChange(elem.target.value, 'startDate')}
              isInvalid={validated && startDateError}
              disabled={isNotFirstVersion || disabled}
              min={minDate}
              max={maxDate}
            />
            {validated && startDateError && (
            <Form.Control.Feedback type="invalid">{startDateError}</Form.Control.Feedback>
            )}
          </Form.Group>
          <Form.Group as={Col} md="6">
            <Form.Label>
              {labels.endDateLabel}
              {' '}
              <Required />
            </Form.Label>
            <Form.Control
              required
              type="date"
              value={data.endDate}
              isInvalid={validated && endDateError}
              onChange={(elem) => onChange(elem.target.value, 'endDate')}
              min={minDate}
              max={maxDate}
              disabled={disabled}
            />
            {validated && endDateError && (
            <Form.Control.Feedback type="invalid">{endDateError}</Form.Control.Feedback>
            )}
          </Form.Group>
        </Row>
        <Row className="mb-4">
          <Form.Group as={Col} md="12">
            <Form.Label>{data.strategy?.type === 'goal'
              ? 'Стратегическая цель развития университета, на которую влияет проект (главная)'
              : 'Стратегический технологический проект'}
            </Form.Label>
            <Form.Control disabled type="text" defaultValue={data.strategyName ?? 'Отсутствует'} />
          </Form.Group>
        </Row>
        {(!portfolioName && labelsCfg) && (
          <Row className="mb-4">
            <Form.Group as={Col} md="12">
              <Form.Check
                type="checkbox"
                label="Кандидат в СТП"
                checked={data.isCandidate}
                onChange={(e) => onChange(e.target.checked, 'isCandidate')}
                disabled={disabled}
              />
            </Form.Group>
          </Row>
        )}
        <Row className='mb-4'>
          <Form.Group as={Col} md='12'>
            <Form.Label className='d-flex align-items-center gap-1'>
                {labels.parentGoalsLabel}
                <Tooltip
                  placement="top"
                  title={<p style={{fontSize: 12}}>
                    Выбор одной или нескольких стратегических целей развития, на которые влияет проект
                  </p>}
                  interactive
                >
                  <HelpOutlineIcon style={{ opacity: '0.66'}} fontSize="inherit" />
                </Tooltip>
            </Form.Label>
            <Select
              placeholder='Выберите из списка'
              noOptionsMessage={() => 'Ничего не найдено'}
              value={parentGoalsOptions.filter(opt => 
                (data?.parentGoals || []).some(dir => dir.id === opt.value.id)
              )}
              onChange={(selectedOptions) => {
                const goal = (selectedOptions || []).map(opt => ({
                  id: opt.value.id,
                  name: opt.value.name
                }));
                onChange(goal, 'parentGoals');
              }}
              menuPortalTarget={document.body}
              options={parentGoalsOptions}
              styles={{menuPortal: (base) => ({...base, zIndex: 5})}}
              isMulti
              closeMenuOnSelect={false}
              isClearable={false}
            />
          </Form.Group>
        </Row>
        <Row className='mb-4'>
          <Form.Group as={Col} md='12'>
            <Form.Label className='d-flex align-items-center gap-1'>
              {labels.parentPoliciesLabel}
            </Form.Label>
            <Select
              placeholder='Выберите из списка'
              noOptionsMessage={() => 'Ничего не найдено'}
              value={parentPoliciesOptions.filter(opt => 
                (data?.parentPolicies || []).some(dir => dir.id === opt.value.id)
              )}
              onChange={(selectedOptions) => {
                const policy = (selectedOptions || []).map(opt => ({
                  id: opt.value.id,
                  name: opt.value.name
                }));
                onChange(policy, 'parentPolicies');
              }}
              menuPortalTarget={document.body}
              options={parentPoliciesOptions}
              styles={{menuPortal: (base) => ({...base, zIndex: 5})}}
              isMulti
              closeMenuOnSelect={false}
              isClearable={false}
            />
          </Form.Group>
        </Row>
        {/* Портфель политики */}
        {/* {!portfolioName && (
          <Row className="mb-4">
            <Form.Group as={Col} md="12">
              <Form.Label>Портфель</Form.Label>
              <Form.Control
                as="select"
                value={data.policyId}
                disabled={data.policyName || disabled}
                onChange={(e) => {
                  const policyId = +e.target.value;
                  if(!policyId){
                    onChange("", 'policyAreaId');
                  }
                  onChange(policyId, 'policyId')
                }}
              >
                <option value="" />
                {policies?.map((t) => (<option value={t.id} key={t.id}>{t.name}</option>))}
              </Form.Control>
            </Form.Group>
          </Row>
        )} */}
        {/* Портфель проектов в СП */}
        {portfolioName && (
          <Row className="mb-4">
            <Form.Group as={Col} md="12">
              <Form.Label>Портфель</Form.Label>
              <Form.Control disabled type="text" defaultValue={portfolioName} />
            </Form.Group>
          </Row>
        )}
        {/* Направления в политике (если входит в портфель политики) */}
        {/* {!!data.policyId && policies.find(policy => policy.id === data.policyId)?.areas?.length && (
          <Row className="mb-4">
            <Form.Group as={Col} md="12">
              <Form.Label>
                Ключевое направление
                <Required />
              </Form.Label>
              <Form.Control
                required
                as="select"
                value={data.policyAreaId}
                onChange={(e) => onChange(e.target.value, 'policyAreaId')}
                disabled={disabled}
              >
                <option value="" />
                {policies.find((p) => p.id === parseInt(data.policyId))?.areas.map((t) => (<option value={t.id} key={t.id}>{t.name}</option>))}
              </Form.Control>
              <Form.Control.Feedback type="invalid">Обязательное поле к заполнению</Form.Control.Feedback>
            </Form.Group>
          </Row>
        )} */}
        {/* <Row className="mb-4">
        <Form.Group as={Col} md="12">
          <Form.Label>Мероприятие Постановления № 729</Form.Label>
          <AddSelection title="Выбор мероприятий Постановления № 729"
            options={letters.map(l => ({ key: l.code, text: `${l.code}) ${l.descr}` }))}
            selectedValues={data.letters}
            onChange={(selectedValues) => onChange(selectedValues, 'letters')}
          />
        </Form.Group>
      </Row> */}
        {/* {withCategory && (
          <Row className="mb-4">
            <Form.Group as={Col} md="12">
              <Form.Label>
                Категория проекта
                <Required />
              </Form.Label>
              <br />
              <Form.Check
                type="radio"
                name="category-none"
                label="Нет категории"
                value={null}
                onChange={changeCategory}
                checked={data.categoryId === null}
                key="category-none"
                inline
                disabled={disabled}
                />
              {categories.map((cat) => (
                <Form.Check
                type="radio"
                  name={`category-${cat.id}`}
                  label={<TooltippedElement tooltip={cat.tooltip}>{cat.title}</TooltippedElement>}
                  value={cat.id}
                  onChange={changeCategory}
                  checked={data.categoryId === cat.id}
                  key={`category-${cat.id}`}
                  inline
                  disabled={disabled}
                  />
                  ))}
                  </Form.Group>
                  </Row>
                  )} */}
        <Row className="mb-4">
          <Form.Group as={Col} md="12">
            <Form.Label>
              {labels.typeLabel}
              {' '}
              <Required />
            </Form.Label>
            <Form.Control
              required
              as="select"
              value={data.typeId}
              disabled={isPresetTypeId || disabled}
              onChange={(e) => handleTypeSelectChange(e.target.value)}
            >
              <option value="" />
              {types.map((t) => (<option value={t.id} key={t.id}>{t.name}</option>))}
            </Form.Control>
            <Form.Control.Feedback type="invalid">Обязательное поле к заполнению</Form.Control.Feedback>
          </Form.Group>
        </Row>
        {isScientific && (
          <Row className='mb-4'>
            <Form.Group as={Col} md='12'>
              <Form.Label className='d-flex align-items-center gap-1'>
                  ГРНТИ
                  {' '}
                  <Required/>
                  <Tooltip
                    placement="top"
                    title={<p style={{fontSize: 12}}>
                      Код программы государственного рубрикатора научно-технической информации в соответствии с
                      {' '}
                      <a
                        href="https://grnti.ru/"
                        target='_blank' rel='noreferrer noopener'
                        style={{color: '#7FC7FF'}}
                      >
                        https://grnti.ru/
                      </a>
                  </p>}
                    interactive
                  >
                    <HelpOutlineIcon style={{ opacity: '0.66'}} fontSize="inherit" />
                  </Tooltip>
              </Form.Label>
              <AsyncSelect
                placeholder="Вводите ключевые слова и выбирайте подходящие направления из списка"
                value={(data?.grntis || []).map(value => ({label: `${value.num} ${value.name}`, value}))}
                onChange={(multiValue) => onChange(multiValue.map(opt => opt.value), 'grntis')}
                noOptionsMessage={() => 'Ничего не найдено'}
                loadOptions={loadGrntiOptions}
                menuPortalTarget={document.body}
                styles={{menuPortal: (base) => ({...base, zIndex: 5})}}
                isMulti
                required
              />
            </Form.Group>
          </Row>
        )}
        {isScientific && (
          <Row className='mb-4'>
            <Form.Group as={Col} md='12'>
              <Form.Label>
                Приоритетные направления научно-технологического развития
                {' '}
                <Required/>
              </Form.Label>
              <Select
                required
                placeholder='Выберите из списка'
                noOptionsMessage={() => 'Ничего не найдено'}
                value={sciTechDirectionsOptions.find(opt => opt.value.id === data?.mainSciTechDirectionId)}
                onChange={(opt) => onChange(opt.value.id, 'mainSciTechDirectionId')}
                menuPortalTarget={document.body}
                options={sciTechDirectionsOptions}
                styles={{menuPortal: (base) => ({...base, zIndex: 5})}}
                isSearchable
              />
            </Form.Group>
          </Row>
        )}
        {isScientific && (
          <Row className='mb-4'>
            <Form.Group as={Col} md='12'>
              <Form.Label className='d-flex align-items-center gap-1'>
                  Соответствие проекта ключевым научно-технологическим направлениям развития УрФУ (КНТН)
                  {' '}
                  <Required/>
              </Form.Label>
              <Select
                required
                placeholder='Выберите из списка'
                noOptionsMessage={() => 'Ничего не найдено'}
                value={sciTechDevUrFUDirectionsOptions.filter(opt => 
                  (data?.sciTechDevUrFUDirections || []).some(dir => dir.id === opt.value.id)
                )}
                onChange={(selectedOptions) => {
                  const directions = (selectedOptions || []).map(opt => ({
                    id: opt.value.id,
                    name: opt.value.name
                  }));
                  onChange(directions, 'sciTechDevUrFUDirections');
                }}
                menuPortalTarget={document.body}
                options={sciTechDevUrFUDirectionsOptions}
                styles={{menuPortal: (base) => ({...base, zIndex: 5})}}
                isMulti
                closeMenuOnSelect={false}
                isClearable={false}
              />
            </Form.Group>
          </Row>
        )}
        {isScientific && (
          <Row className='mb-4'>
            <Form.Group as={Col} md='12'>
              <Form.Label className='d-flex align-items-center gap-1'>
                  Соответствие проекта  Большим вызовами СНТР
                  {' '}
                  <Required/>
              </Form.Label>
              <Select
                required
                placeholder='Выберите из списка'
                noOptionsMessage={() => 'Ничего не найдено'}
                value={majorSciTechDevChallengesOptions.find(opt => opt.value.id === data?.majorSciTechDevChallengeId)}
                onChange={(opt) => onChange(opt.value.id, 'majorSciTechDevChallengeId')}
                menuPortalTarget={document.body}
                options={majorSciTechDevChallengesOptions}
                styles={{menuPortal: (base) => ({...base, zIndex: 5})}}
                isSearchable
              />
            </Form.Group>
          </Row>
        )}
        {isScientific && (
          <Row className='mb-4'>
            <Form.Group as={Col} md='12'>
              <Form.Label className='d-flex align-items-center gap-1'>
                  Национальный проект технологического лидерства (НПТЛ)
                  {' '}
                  <Required/>
              </Form.Label>
              <Select
                required
                placeholder='Выберите из списка'
                noOptionsMessage={() => 'Ничего не найдено'}
                value={nationalProjTechLeadsOptions.filter(opt => 
                  (data?.nationalProjTechLeads || []).some(dir => dir.id === opt.value.id)
                )}
                onChange={(selectedOptions) => {
                  const proj = (selectedOptions || []).map(opt => ({
                    id: opt.value.id,
                    name: opt.value.name
                  }));
                  onChange(proj, 'nationalProjTechLeads');
                }}
                menuPortalTarget={document.body}
                options={nationalProjTechLeadsOptions}
                styles={{menuPortal: (base) => ({...base, zIndex: 5})}}
                isMulti
                closeMenuOnSelect={false}
                isClearable={false}
              />
            </Form.Group>
          </Row>
        )}
        {isScientific && (
          <Card className='mb-4'>
            <Card.Header as={Card.Title} className='p-4' style={{fontSize: 16}}>
              Соответствие перечню важнейших наукоемких технологий
            </Card.Header>
            <Card.Body>
              <Row className='mb-4'>
                <Form.Group as={Col}>
                  <Form.Label>
                    Критические технологии
                    {' '}
                    <Required/>
                  </Form.Label>
                  <Select
                    placeholder='Выберите из списка'
                    noOptionsMessage={() => 'Ничего не найдено'}
                    options={criticalTechsOptions}
                    value={criticalTechsOptions.find(opt => opt.value.id === data?.criticalTechId)}
                    onChange={(opt) => onChange(opt.value.id, 'criticalTechId')}
                    isSearchable
                    required
                  />
                </Form.Group>
              </Row>
              <Row>
                <Form.Group as={Col}>
                  <Form.Label>
                    Сквозные технологии
                    {' '}
                    <Required/>
                  </Form.Label>
                  <Select
                    placeholder='Выберите из списка'
                    noOptionsMessage={() => 'Ничего не найдено'}
                    options={endToEndTechsOptions}
                    value={endToEndTechsOptions.find(opt => opt.value.id === data?.endToEndTechId)}
                    onChange={(opt) => onChange(opt.value.id, 'endToEndTechId')}
                    isSearchable
                    required
                  />
                </Form.Group>
              </Row>
            </Card.Body>
          </Card>
        )}
      <Row className='mb-4'>
        <Form.Group as={Col} md='12'>
          <Form.Label className='d-flex align-items-center gap-1'>
            {labels.partnersLabel}
            <Tooltip
              placement="top"
              title={<p style={{fontSize: 12}}>
                Укажите ИНН и выберите организацию-партнер из списка
                </p>}
              interactive
            >
              <HelpOutlineIcon style={{ opacity: '0.66'}} fontSize="inherit" />
            </Tooltip>
          </Form.Label>
          <AsyncSelect
            placeholder='Укажите ИНН и выберите организацию-партнер из списка'
            noOptionsMessage={() => 'Ничего не найдено'}
            loadOptions={loadPartnersOptions}
            value={(data?.partners || []).map(partner => ({
              label: `${partner.inn} ${partner.title}`,
              value: partner
            }))}
            onChange={(selectedOptions) => {
              const partners = (selectedOptions || []).map(opt => ({
                id: opt.value.id,
                inn: opt.value.inn,
                title: opt.value.title,
                kpp: opt.value.kpp,
                categoryId: opt.value.categoryId
              }));
              onChange(partners, 'partners');
            }}
            menuPortalTarget={document.body}
            styles={{menuPortal: (base) => ({...base, zIndex: 5})}}
            isMulti
            closeMenuOnSelect={false}
            isClearable={false}
            disabled={disabled}
          />
        </Form.Group>
      </Row>
      <Row>
        <Form.Group as={Col} md="12">
          <Form.Label>Показатели программы {!!data.strategyId && <Required/>}</Form.Label>
          <AddSelection
            title="Выбор показателей программы"
            options={kpis.filter((k) => data.kpis
              .find((dk) => dk === k.id) || (k.startYear ?? 2020) <= currentYear && (k.endYear ?? 2030) >= currentYear)
              .map((k) => ({
                key: k.id,
                text: `${k.num} ${k.name}`,
              }))}
            selectedValues={data.kpis}
            required={!!data.strategyId}
            disabled={disabled}
            onChange={(selectedValues) => onChange(selectedValues, 'kpis')}
          />
        </Form.Group>
      </Row>
    </Card.Body>
  </Card>);
}
